import { AppBar, Typography, styled, IconButton } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import { AttributesMixin, define, Linked, Store, type } from '@type-r/models';
import { useIO, useLinked, useModel } from '@type-r/react';
import { asCurrency, asDate, Flex, Loading, Page, SelectSortBy, TilesList, Toolbar, AddFilter, FiltersList, CsvDownloader, CsvMapping, asTimelessDate } from 'lqx-react';
import { asFilenameDateTime } from 'lqx-react/src';
import * as React from 'react';
import { useRef } from 'react';
import { CompanyModel, globalStore } from 'api';
import { InmatchConversation, Transaction, BankTransactionFilters, fetchTransactionByType } from 'src/app/pages/inmatch/api';
import { createFiscalCalendarMap, FiscalCalendar } from '../../api/fiscal-calendar';
import { ArMoveDialog } from '../nonAr/arMoveDialog';
import { ArPaymentTile } from './arPaymentTile'
import { NonArPaymentTile } from './nonArPaymentTile'
import { UacTransactionTile } from './uacTransacitonTile'

interface State extends AttributesMixin<typeof State>{}

const sortRemittanceOptions = {
    statementDate: 'Statement Date',
    updatedOn: 'Last Updated Date',
    unreconciledAmount: 'Unreconciled Amount',
};

const csvFields: CsvMapping<Transaction> = {
    'Id' : 'id',
    'Type' : 'currentType',
    'Status': 'status',
    'Customer' : 'customerName',
    'Supplier' : 'supplierName',
    'Amount' : x => asCurrency(x.amount, x.currency),
    'Outstanding Amount' : x => asCurrency(x.unreconciledAmount, x.currency),
    'Creation Date' : x => asTimelessDate(x.createdOn),
    'Statement Date' : x => asTimelessDate(x.statementDate),
    'Value Date' : x => asTimelessDate(x.valueDate),
    'BAI Code' : 'baiCode',
    'Currency' : 'currency',
    'Debtor Bank Name' : 'debtorBankName',
    'Debtor Account' : 'debtorAccount',
    'Creditor Account' : 'creditorAccount',
    'Creditor Bank Name' : 'creditorBankName',
    'Entry Reference Number' : 'entryReferenceNumber',
    'Additional Entry Info' : 'additionalEntryInfo',
    'Remittance Info Unstructured': 'remittanceInfoUnstructured',

};

@define class State extends Store {
    static attributes = {
        transactions: [ Transaction ],
        companies: [CompanyModel],
        fiscalCalendars: [FiscalCalendar],
        fiscalCalendarMap: Object,
        filter : type(BankTransactionFilters),
        sortBy : {
            attr: 'statementDate',
            asc : false
        },
        exportData: [Transaction],
        preparingExport: Boolean,
    }

    async fetch(category){
        const transactionType = category === 'all' ? '' : category;

        this.companies = globalStore.companies;

        await Promise.all([
            this.transactions.set(await fetchTransactionByType(transactionType)),
            this.fiscalCalendars.fetch().then(calendars => {
                this.fiscalCalendarMap = createFiscalCalendarMap(calendars);
            })
        ]);

        this.filter.initializeOptions(this.fiscalCalendars);

        return this;
    }
}

export const TransactionPage = ({ $category } : {
    $category : Linked<string>
}) => {
    const state = useModel( State );

    const csvDownloader = useRef<CsvDownloader<Transaction>>(null);

    const isReady = useIO( async () => {
        await state.fetch($category.value);
    }, [$category['_changeToken']]);

    const sortedAndFiltered = React.useMemo( () => {
        const { attr, asc } = state.sortBy;
        return state.transactions
            .filter(p => p.filter(state.filter, state.fiscalCalendarMap) )
            .sort( Transaction.comparator( attr as any, asc ) );
    },
    [ state.transactions['_changeToken'], state.sortBy['_changeToken'], state.filter["_changeToken"] ]);

    const $paymentToMove =  useLinked<Transaction>(null) as Linked<Transaction>;

    const movePayment = (payment: Transaction) => {
        $paymentToMove.set(payment);
    };

    const paymentMoved = (paymentId: number) => {
        state.transactions.remove(paymentId);
    };

    React.useEffect(() => {
        state.exportData.set(sortedAndFiltered);
    }, [sortedAndFiltered])

    const icnBtnClicked = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const transactionType = $category.value === 'all' ? '' : $category.value;
        const { attr, asc } = state.sortBy;

        state.$.preparingExport.set(true);
        state.exportData.set(await fetchTransactionByType(transactionType, true));
        state.exportData.set(state.exportData
            .filter(p => p.filter(state.filter, state.fiscalCalendarMap))
            .sort(Transaction.comparator(attr as any, asc))
        );

        state.$.preparingExport.set(false);

        csvDownloader.current.handleClick();
    };

    return (
        isReady ?
        <>
            { state.preparingExport ? <Loading /> : 
            <Page
                maxWidth={false}
                top={
                    <AppBar position="static" color="transparent">
                        <Toolbar>
                            <AddFilter
                                filter={state.filter}
                                filterAllPlaceholder="Filter by..."
                            />

                            <Flex grow />

                            <SelectSortBy $attr={ state.sortBy.$.attr } $asc={ state.sortBy.$.asc } options={ sortRemittanceOptions } />

                            <CsvDownloader ref={csvDownloader} filename={ `transactions_${$category.value}_${asFilenameDateTime( new Date() )}` } mapping={ csvFields } data={ state.exportData.models }>
                                <IconButton aria-label="Download" size="small" onClick={icnBtnClicked}>
                                    <GetAppIcon />
                                </IconButton>
                            </CsvDownloader>
                        </Toolbar>
                        <FilterListWrapper>
                            <FiltersList filter={state.filter} />
                        </FilterListWrapper>
                    </AppBar>

                }
            >
                { sortedAndFiltered.length ?
                    <TilesList
                        items={ sortedAndFiltered }
                        itemSize={ 80 }
                        renderer={ TransactionTile(movePayment) }
                    />
                :
                    <Flex center stretch>
                        <Typography variant="h4">
                            No results found.
                        </Typography>
                    </Flex>
                }

                { $paymentToMove.value ? <ArMoveDialog $payment={ $paymentToMove } moveTo="ar" paymentMoved={ paymentMoved } /> : undefined }

            </Page>     
            }       
        </>

        :
            <Loading />
    )
}

const TransactionTile = (move: (payment: Transaction) => void) => ({ data, style, index }) => {
    const transaction : Transaction = data.items[ index ];

    return (
        transaction.currentType === 'NonAr' ? 
            <NonArPaymentTile move={move} transaction={transaction} style={style} /> :
        transaction.currentType === 'Uac' ? 
            <UacTransactionTile move={move} transaction={transaction} style={style} /> :
        <ArPaymentTile transaction={transaction} style={style} />
    );
}

export const FilterListWrapper =  styled( 'div' )({
    paddingLeft: 24,
    background: 'white'
  })
